import { Trans, useTranslation } from "react-i18next";
import { Stack, Typography } from "@mui/material";
import { convertPxToRem } from "../../../../utils";
import {
  DirectToDoctorPrimarySymptom,
  DirectToDoctorPrimarySymptomOption,
  MemberProductPaymentRatesChargeCode,
  MemberProfile,
} from "../../../../services/core-api-adapter";
import { trackUserInteraction } from "@/services/analytics-adapter";
import {
  useInsightsAdapter,
  MembershipType,
} from "@/services/insights-adapter";
import ItemPill from "@/features/Common/components/ItemPill";
import { defaultFullscreenPageStyling } from "@/theme";
import CircledBackButton from "@/features/Common/components/CircledBackButton";
import { PRIMARY_SYMPTOM_PILL_MAP_V2 } from "@/features/Common/constants";

interface DirectToDoctorPrimarySymptomsProps {
  primarySymptoms: DirectToDoctorPrimarySymptom[];
  memberProfile: MemberProfile;
  onBackButtonClick: () => void;
  onPrimarySymptomButtonClick: (primarySymptomId: string) => void;
  memberId?: string;
  membershipType?: (typeof MembershipType)[keyof typeof MembershipType];
}

export default function DirectToDoctorPrimarySymptoms({
  primarySymptoms,
  memberProfile,
  onBackButtonClick,
  onPrimarySymptomButtonClick,
  memberId: memberIdProp,
  membershipType: membershipTypeProp,
}: DirectToDoctorPrimarySymptomsProps) {
  const { t } = useTranslation();
  const { trackConditionsWeTreat } = useInsightsAdapter();

  const directToDoctorPaymentRates =
    memberProfile.consultation.consultationRates.find(
      (rate) =>
        rate.chargeCode ===
        MemberProductPaymentRatesChargeCode.DIGITAL_DOCTOR_CONSULT
    );

  function filterSymptomsByCategory(
    category: "sexualHealth" | "generalConditions"
  ): DirectToDoctorPrimarySymptom[] {
    const sexualHealthSymptoms = [
      DirectToDoctorPrimarySymptomOption.CONTRACEPTION,
      DirectToDoctorPrimarySymptomOption.GENITAL_INFECTIONS,
    ];

    return primarySymptoms.filter((symptom) =>
      category === "sexualHealth"
        ? sexualHealthSymptoms.includes(symptom.name)
        : !sexualHealthSymptoms.includes(symptom.name)
    ) as DirectToDoctorPrimarySymptom[];
  }

  function getSexualHealthSymptoms(): DirectToDoctorPrimarySymptom[] {
    return filterSymptomsByCategory("sexualHealth");
  }

  function getGeneralConditionsSymptoms(): DirectToDoctorPrimarySymptom[] {
    return filterSymptomsByCategory("generalConditions");
  }

  function onPrimarySymptomPillClick(
    primarySymptom: DirectToDoctorPrimarySymptom
  ) {
    trackConditionsWeTreat({
      serviceType: primarySymptom.name,
      member_id: memberIdProp || "",
      membershipType: membershipTypeProp || MembershipType.MainMember,
      event_datetime: new Date().toISOString(),
    });
    trackUserInteraction({
      linkText: `DirectToDoctorPrimarySymptomsList | ${primarySymptom.name}`,
      linkIntent: "navigational",
      linkScope: "button",
    });
    onPrimarySymptomButtonClick(primarySymptom.id);
  }

  return (
    <Stack spacing={3} sx={{ ...defaultFullscreenPageStyling }}>
      <CircledBackButton showLabel onClick={onBackButtonClick} />
      <Stack textAlign="left" spacing={3}>
        <Stack spacing={1}>
          <Typography variant="h2">
            {t("DirectToDoctor.primarySymptoms.title")}
          </Typography>
          <Trans>
            {directToDoctorPaymentRates?.isDiscountApplied ? (
              <Typography variant="h4" fontWeight={400}>
                <Trans
                  i18nKey="DirectToDoctor.primarySymptoms.modal.discount.subtitle"
                  values={{
                    amountFormatted:
                      directToDoctorPaymentRates?.amountFormatted,
                    originalAmountFormatted:
                      directToDoctorPaymentRates?.originalAmountFormatted,
                  }}
                >
                  <Typography variant="body2" color="neutral.500"></Typography>
                </Trans>
              </Typography>
            ) : (
              <Typography variant="h4" fontWeight={400}>
                {t("DirectToDoctor.primarySymptoms.modal.subtitle", {
                  amountFormatted: directToDoctorPaymentRates?.amountFormatted,
                })}
              </Typography>
            )}
          </Trans>
        </Stack>
        {primarySymptoms && (
          <Stack spacing={2}>
            <Typography fontWeight="bold">
              {t("DirectToDoctor.primarySymptoms.category.sexualHealth.title")}
            </Typography>
            {getSexualHealthSymptoms().map((primarySymptom) => (
              <ItemPill
                key={primarySymptom.id}
                onClick={() => onPrimarySymptomPillClick(primarySymptom)}
                itemMap={PRIMARY_SYMPTOM_PILL_MAP_V2}
                selectedItem={primarySymptom}
                titleStyles={{
                  fontSize: convertPxToRem(16),
                  fontWeight: 600,
                }}
                shouldShowSubtitle={true}
                shouldShowArrow={true}
              />
            ))}
            <Typography fontWeight="bold">
              {t(
                "DirectToDoctor.primarySymptoms.category.generalConditions.title"
              )}
            </Typography>
            {getGeneralConditionsSymptoms().map((primarySymptom) => (
              <ItemPill
                key={primarySymptom.id}
                onClick={() => onPrimarySymptomPillClick(primarySymptom)}
                itemMap={PRIMARY_SYMPTOM_PILL_MAP_V2}
                selectedItem={primarySymptom}
                titleStyles={{
                  fontSize: convertPxToRem(16),
                  fontWeight: 600,
                }}
                shouldShowSubtitle={true}
                shouldShowArrow={true}
              />
            ))}
          </Stack>
        )}
      </Stack>
    </Stack>
  );
}
